import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Award, Wrench, Clock, Phone, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { features, services as mockServices } from '../data/mock';
import { getServices } from '../services/productApi';

const DatenschutzSocialMedia = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState(mockServices);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const servicesData = await getServices();
        if (servicesData?.length > 0) {
          setServices(servicesData);
        }
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };
    loadServices();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Datenschutz Social Media Hauptbereich */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          
          {/* Überschrift */}
          <h1 className="text-lg sm:text-xl font-semibold text-white bg-gray-900 px-6 py-3 rounded-t-lg">
            Datenschutz Social Media – Jannesmann Baumaschinen
          </h1>

          {/* Textbereich */}
          <div className="bg-gray-50 rounded-b-xl border border-gray-200 border-t-0 p-6 md:p-8">

            {/* Einleitung */}
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-10">
              Wir sind in verschiedenen sozialen Netzwerken präsent, um mit Ihnen in Kontakt zu treten und Sie über unsere Produkte und Dienstleistungen zu informieren. Nachfolgend informieren wir Sie über die Datenverarbeitung im Zusammenhang mit unseren Social-Media-Auftritten.
            </p>

            {/* 1. Verantwortlicher */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                1. Verantwortlicher
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Jannesmann Handels und Import GbR<br />
                Gesellschafter: Yasar Kartal, Jan Hassel und Ayten Celik<br />
                Poststraße 6<br />
                46236 Bottrop<br />
                E-Mail: info@jannesmann.de
              </p>
            </div>

            {/* 2. Gemeinsame Verantwortlichkeit */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                2. Gemeinsame Verantwortlichkeit
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Bei der Nutzung von Social-Media-Plattformen besteht in der Regel eine gemeinsame Verantwortlichkeit zwischen uns und dem jeweiligen Plattformbetreiber gemäß Art. 26 DSGVO. Die Plattformbetreiber erheben und verarbeiten Ihre Daten auch zu eigenen Zwecken, auf die wir keinen Einfluss haben.
              </p>
            </div>

            {/* 3. Unsere Social-Media-Präsenzen */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                3. Unsere Social-Media-Präsenzen
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                Wir unterhalten Unternehmensseiten auf folgenden Plattformen:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>Facebook</strong> – Betreiber: Meta Platforms Ireland Ltd., 4 Grand Canal Square, Dublin 2, Irland</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>Instagram</strong> – Betreiber: Meta Platforms Ireland Ltd., 4 Grand Canal Square, Dublin 2, Irland</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>YouTube</strong> – Betreiber: Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>TikTok</strong> – Betreiber: TikTok Technology Limited, 10 Earlsfort Terrace, Dublin, D02 T380, Irland</span>
                </li>
              </ul>
            </div>

            {/* 4. Datenverarbeitung */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                4. Datenverarbeitung durch Plattformbetreiber
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Die Plattformbetreiber verarbeiten Ihre Daten zu Werbezwecken, Marktforschung und zur bedarfsgerechten Gestaltung ihrer Plattformen. Dabei werden auch Cookies gesetzt und Ihr Nutzungsverhalten analysiert. Dies gilt auch dann, wenn Sie kein Konto bei der jeweiligen Plattform haben. Detaillierte Informationen zur Datenverarbeitung durch die Plattformbetreiber finden Sie in deren jeweiligen Datenschutzerklärungen.
              </p>
            </div>

            {/* 5. Ihre Rechte */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                5. Ihre Rechte
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch gegen die Verarbeitung Ihrer personenbezogenen Daten. Diese Rechte können Sie gegenüber uns und/oder dem jeweiligen Plattformbetreiber geltend machen.
              </p>
            </div>

            {/* 6. Kontakt */}
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                6. Kontakt
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Bei Fragen zum Datenschutz im Zusammenhang mit unseren Social-Media-Präsenzen erreichen Sie uns unter: <a href="mailto:info@jannesmann.de" className="text-red-600 hover:underline">info@jannesmann.de</a>
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Info-Bereich: 4 Vorteile + 3 Leistungen + CTA */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="bg-gray-50 rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6">
            
            {/* 4 Vorteile */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {features.map((feature, index) => {
                const IconComponent = { Award, Wrench, Clock, HeadphonesIcon: Phone }[feature.icon];
                return (
                  <Card key={index} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                    <CardContent className="pt-6 pb-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                          <IconComponent className="text-red-600" size={24} />
                        </div>
                        <h3 className="font-semibold text-sm sm:text-lg mb-2">{feature.title}</h3>
                        <p className="text-gray-600 text-xs sm:text-sm">{feature.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* 3 Leistungen */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {services.map((service) => (
                <Card key={service.id} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                        {service.id === 1 && <Award className="text-red-600" size={24} />}
                        {service.id === 2 && <Phone className="text-red-600" size={24} />}
                        {service.id === 3 && <Wrench className="text-red-600" size={24} />}
                      </div>
                      <h3 className="font-semibold text-sm sm:text-lg mb-2">{service.title}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm mb-3">{service.description}</p>
                      <ul className="space-y-1 text-left w-full">
                        {service.features.slice(0, 3).map((feat, idx) => (
                          <li key={idx} className="flex items-start text-xs sm:text-sm">
                            <CheckCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={14} />
                            <span className="text-gray-700">{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* CTA */}
            <Card className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
              <CardContent className="pt-8 pb-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                    <ArrowRight className="text-red-600" size={24} />
                  </div>
                  <h3 className="font-semibold text-lg sm:text-xl mb-2">{t('cta.readyForProject')}</h3>
                  <p className="text-gray-600 text-sm sm:text-base mb-4 max-w-xl">{t('cta.contactUs')}</p>
                  <Link to="/kontakt">
                    <Button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center">
                      {t('cta.getInTouch')}
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Zwei Videos */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="grid grid-cols-2 gap-4 md:gap-6">
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video autoPlay muted loop playsInline preload="metadata" className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover" webkit-playsinline="true">
                <source src="/videos/video2.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video autoPlay muted loop playsInline preload="metadata" className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover" webkit-playsinline="true">
                <source src="/videos/video3.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DatenschutzSocialMedia;
